import React, { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { useSidebar } from "../hooks";
import { API_BASE_URL } from "../config";
import "./css/Settings.css";
import { Session } from "./utils/session";

const authHeaders = () => {
  const token = Session.getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

function getSettingItem(data, key) {
  if (!Array.isArray(data)) return null;
  return (
    data.find((x) => {
      const k = (x.SKey ?? x.skey ?? x.sKey ?? "").toString().toLowerCase();
      return k === key.toLowerCase();
    }) ?? null
  );
}

function getSid(item) {
  return item?.Sid ?? item?.sid ?? null;
}

function getValue(item) {
  if (!item) return "";
  const v = item.SValue ?? item.svalue ?? item.sValue;
  return v == null ? "" : String(v);
}

function Settings() {
  const isAdmin = Session.isAdmin();
  const { toggleSidebar } = useSidebar();

  const [appPassword, setAppPassword] = useState("");
  const [appPasswordSid, setAppPasswordSid] = useState(null);
  const [originalAppPassword, setOriginalAppPassword] = useState("");

  const [windowIdleLoginTimeout, setWindowIdleLoginTimeout] = useState("45");
  const [idleSid, setIdleSid] = useState(null);
  const [originalIdle, setOriginalIdle] = useState("45");

  const [statusMsg, setStatusMsg] = useState("");
  const [statusType, setStatusType] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const unsaved =
    appPassword !== originalAppPassword ||
    String(windowIdleLoginTimeout) !== String(originalIdle);

  function showStatus(msg, type) {
    setStatusMsg(msg);
    setStatusType(type);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setStatusMsg("");
      setStatusType("");
    }, 2500);
  }

  useEffect(() => {
    if (!isAdmin) return undefined;
    let mounted = true;

    async function loadSettings() {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/Setting/All_Setting`, {
          method: "GET",
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error("Failed to load settings");
        const data = await res.json();
        if (!mounted) return;

        const pwdItem = getSettingItem(data, "AppUserUnablePassword");
        const idleItem = getSettingItem(data, "WindowIdleLoginTimeout");

        const pwd = getValue(pwdItem);
        const idle = getValue(idleItem) || "45";

        setAppPasswordSid(getSid(pwdItem));
        setAppPassword(pwd);
        setOriginalAppPassword(pwd);

        setIdleSid(getSid(idleItem));
        setWindowIdleLoginTimeout(idle);
        setOriginalIdle(idle);
      } catch {
        if (mounted) showStatus("Failed to load settings.", "error");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSettings();
    return () => {
      mounted = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAdmin]);

  async function updateBySid(sid, value) {
    const res = await fetch(`${API_BASE_URL}/api/Setting/Update_Setting_Value`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ Sid: sid, SValue: value }),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      const msg =
        (body && (body.Message || body.message)) || "Failed to update setting.";
      throw new Error(msg);
    }
    return body;
  }

  async function saveSettings() {
    if (saving || !unsaved) return;

    if (appPassword !== originalAppPassword && appPasswordSid == null) {
      showStatus("App password setting not found in database.", "error");
      return;
    }
    if (
      String(windowIdleLoginTimeout) !== String(originalIdle) &&
      idleSid == null
    ) {
      showStatus("Idle timeout setting not found in database.", "error");
      return;
    }

    const idleVal = Number.parseInt(windowIdleLoginTimeout, 10);
    if (
      String(windowIdleLoginTimeout) !== String(originalIdle) &&
      (Number.isNaN(idleVal) || idleVal < 1)
    ) {
      showStatus("Idle login timeout must be a number ≥ 1.", "error");
      return;
    }

    setSaving(true);
    try {
      if (appPassword !== originalAppPassword) {
        await updateBySid(appPasswordSid, appPassword);
      }
      if (String(windowIdleLoginTimeout) !== String(originalIdle)) {
        await updateBySid(idleSid, String(idleVal));
      }
      setOriginalAppPassword(appPassword);
      setOriginalIdle(String(idleVal || windowIdleLoginTimeout));
      showStatus("Settings saved.", "success");
    } catch (err) {
      showStatus(err?.message || "Failed to save settings.", "error");
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div>
      <div id="sidebar-overlay" />
      <Sidebar />
      <div id="content">
        <div className="d-flex align-items-center mb-3">
          <button
            type="button"
            id="sidebarCollapse"
            className="btn btn-toggle me-3"
            onClick={toggleSidebar}
          >
            <i className="fas fa-bars" />
          </button>
        </div>

        <div className="product-page-wrapper">
          <div className="main-card">
            <div className="panel panel-default">
              <div className="panel-body p-0">
                <div className="mb-0 p-4">
                  <div className="d-flex justify-content-end mb-2">
                    <div className={`toast-container ${statusMsg ? "show" : ""}`}>
                      {statusMsg && (
                        <div className={`toast-message ${statusType || "info"}`}>
                          {statusMsg}
                          <button
                            className="toast-close"
                            onClick={() => {
                              setStatusMsg("");
                              setStatusType("");
                            }}
                            aria-label="Close"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <h4 className="mb-3">Settings</h4>

                  {loading ? (
                    <p className="text-muted">Loading…</p>
                  ) : (
                    <>
                      <div className="form-group">
                        <label className="setting-row" htmlFor="appPassword">
                          <div className="setting-label">
                            <span className="title">App password</span>
                            <span className="desc">
                              Desktop unlock password (
                              <code>AppUserUnablePassword</code>)
                            </span>
                          </div>
                          <input
                            id="appPassword"
                            type="text"
                            autoComplete="off"
                            className="form-control"
                            style={{ maxWidth: 220 }}
                            placeholder="Enter password"
                            value={appPassword}
                            onChange={(e) => setAppPassword(e.target.value)}
                          />
                        </label>
                      </div>

                      <div className="form-group" style={{ marginTop: 12 }}>
                        <label
                          className="setting-row"
                          htmlFor="windowIdleLoginTimeout"
                        >
                          <div className="setting-label">
                            <span className="title">
                              Idle login timeout (minutes)
                            </span>
                            <span className="desc">
                              Idle for this many minutes → auto logout (even if
                              a task is running)
                            </span>
                          </div>
                          <input
                            id="windowIdleLoginTimeout"
                            type="number"
                            min="1"
                            className="form-control"
                            style={{ maxWidth: 220 }}
                            placeholder="e.g. 45"
                            value={windowIdleLoginTimeout}
                            onChange={(e) =>
                              setWindowIdleLoginTimeout(e.target.value)
                            }
                          />
                        </label>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="d-flex p-4 border-top">
                <button
                  type="button"
                  className="btn btn-primary saveUnsaved"
                  onClick={saveSettings}
                  disabled={!unsaved || saving || loading}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
